import * as XLSX from "xlsx";
import type { RaRecord } from "react-admin";

import { formatExportFilenameTimestamp } from "./date-format";
import type { TallyConfig } from "@/types/tally-config";
import {
  buildTallyExportPreview,
  previewRowsToVoucherRows,
  type TallyExportModule,
} from "./tally-export-preview";
import type { TallyNarrationOptions } from "./tally-narration-options";

export type { TallyExportModule } from "./tally-export-preview";
export type { TallyNarrationOptions } from "./tally-narration-options";
export {
  auditPreviewRows,
  buildManualVoucherRows,
  buildTallyExportPreview,
  extractRentMissingHalls,
  countVouchersWithIssues,
  excludeVouchersWithIssues,
  filterExportRowsByIgnoredVouchers,
  hasBlockingPreviewIssues,
  listProblemVouchers,
  previewRowsToVoucherRows,
  rentHallMappingOptions,
  type TallyExportPreview,
  type TallyManualVoucherForm,
  type TallyPreviewRow,
  type TallyReceiptOverride,
  type TallySkippedReceipt,
} from "./tally-export-preview";

const SHEET_NAME = "Accounting Voucher";

/** Tally Default Template column headers (column 5 uses non-breaking space). */
export const TALLY_VOUCHER_HEADERS = [
  "Voucher Date",
  "Voucher Type Name",
  "Voucher Number",
  "Ledger Name",
  "Ledger Amount\xa0",
  "Ledger Amount Dr/Cr",
  "Voucher Narration",
  "Ledger Narration",
  "Category Name",
  "Cost Allocation for - Cost Centre",
  "Cost Allocation for - Amount",
] as const;

export type TallyVoucherRow = {
  voucherDate: Date | string | null;
  voucherType: string;
  voucherNumber: string;
  ledgerName: string;
  amount: number;
  drCr: "Dr" | "Cr";
  voucherNarration?: string | null;
  ledgerNarration?: string | null;
  categoryName?: string | null;
  costCentre?: string | null;
  costAmount?: number | null;
};

function tallyTrim(value: unknown): string {
  return String(value ?? "").trim();
}

export function buildFlatRows(
  module: TallyExportModule,
  records: RaRecord[],
  config: TallyConfig,
  narrationOptions?: TallyNarrationOptions
): TallyVoucherRow[] {
  const preview = buildTallyExportPreview(module, records, config, {}, narrationOptions);
  return previewRowsToVoucherRows(preview.rows);
}

export function buildLagatTallyRows(
  records: RaRecord[],
  config: TallyConfig,
  narrationOptions?: TallyNarrationOptions
): TallyVoucherRow[] {
  return buildFlatRows("lagat", records, config, narrationOptions);
}

export function buildSabilTallyRows(
  records: RaRecord[],
  config: TallyConfig,
  narrationOptions?: TallyNarrationOptions
): TallyVoucherRow[] {
  return buildFlatRows("sabil", records, config, narrationOptions);
}

export function buildRentTallyRows(
  records: RaRecord[],
  config: TallyConfig,
  narrationOptions?: TallyNarrationOptions
): TallyVoucherRow[] {
  return buildFlatRows("rent", records, config, narrationOptions);
}

export function buildNiyaazTallyRows(
  records: RaRecord[],
  config: TallyConfig,
  narrationOptions?: TallyNarrationOptions
): TallyVoucherRow[] {
  return buildFlatRows("niyaaz", records, config, narrationOptions);
}

export function buildYearlyNiyaazTallyRows(
  records: RaRecord[],
  config: TallyConfig,
  narrationOptions?: TallyNarrationOptions
): TallyVoucherRow[] {
  return buildFlatRows("yearlyNiyaaz", records, config, narrationOptions);
}

export function buildFmbTallyRows(
  records: RaRecord[],
  config: TallyConfig,
  narrationOptions?: TallyNarrationOptions
): TallyVoucherRow[] {
  return buildFlatRows("fmb", records, config, narrationOptions);
}

/** One Excel row; cost columns only on Cr lines (matches Tally sample import). */
export function tallyRowToSheetRecord(r: TallyVoucherRow): Record<string, unknown> {
  const isCr = r.drCr === "Cr";
  const hasCost =
    isCr &&
    r.costAmount != null &&
    r.costAmount > 0 &&
    Boolean(tallyTrim(r.categoryName)) &&
    Boolean(tallyTrim(r.costCentre));

  return {
    [TALLY_VOUCHER_HEADERS[0]]: r.voucherDate,
    [TALLY_VOUCHER_HEADERS[1]]: r.voucherType,
    [TALLY_VOUCHER_HEADERS[2]]: r.voucherNumber,
    [TALLY_VOUCHER_HEADERS[3]]: r.ledgerName,
    [TALLY_VOUCHER_HEADERS[4]]: r.amount,
    [TALLY_VOUCHER_HEADERS[5]]: r.drCr,
    [TALLY_VOUCHER_HEADERS[6]]: r.voucherNarration ?? "",
    [TALLY_VOUCHER_HEADERS[7]]: r.ledgerNarration ?? "",
    [TALLY_VOUCHER_HEADERS[8]]: hasCost ? tallyTrim(r.categoryName) : "",
    [TALLY_VOUCHER_HEADERS[9]]: hasCost ? tallyTrim(r.costCentre) : "",
    [TALLY_VOUCHER_HEADERS[10]]: hasCost ? r.costAmount : "",
  };
}

export function exportToTallyExcel(
  rows: TallyVoucherRow[],
  options: { filenamePrefix?: string } = {}
): void {
  const { filenamePrefix = "tally-export" } = options;

  const sheetRows = rows.map((r) => tallyRowToSheetRecord(r));

  const worksheet = XLSX.utils.json_to_sheet(sheetRows as Record<string, unknown>[]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, SHEET_NAME);

  for (let c = 0; c < TALLY_VOUCHER_HEADERS.length; c += 1) {
    const col = XLSX.utils.encode_col(c);
    const cell = worksheet[`${col}1`];
    if (cell) {
      cell.v = TALLY_VOUCHER_HEADERS[c];
    }
  }

  worksheet["!cols"] = TALLY_VOUCHER_HEADERS.map(() => ({ wch: 22 }));

  const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbout], { type: "application/octet-stream" });
  const filename = `${filenamePrefix}_${formatExportFilenameTimestamp()}.xlsx`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
